import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Warning, ShieldCheck } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import api from '../lib/api';

const ResetApprove = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');
  const action = params.get('action') || 'approve'; // approve | deny

  const [status, setStatus] = useState('processing'); // processing | success | denied | error
  const [message, setMessage] = useState('');
  const [errorDetail, setErrorDetail] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return; // strict-mode guard
    calledRef.current = true;
    if (!token) {
      setStatus('error');
      setErrorDetail('Missing or invalid reset link.');
      return;
    }
    (async () => {
      try {
        const res = await api.post('/auth/reset-approve', { token, action });
        setMessage(res.data.message || '');
        setStatus(action === 'deny' ? 'denied' : 'success');
      } catch (err) {
        const detail = err.response?.data?.detail;
        setErrorDetail(typeof detail === 'string' ? detail : 'Reset link is invalid or has already been used.');
        setStatus('error');
      }
    })();
  }, [token, action]);

  const renderState = () => {
    if (status === 'processing') {
      return (
        <>
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-yellow-500/15 mb-5">
            <ShieldCheck size={42} weight="fill" className="text-yellow-400 animate-pulse" />
          </div>
          <h2 className="text-2xl font-unbounded font-bold text-white mb-2">
            {action === 'deny' ? 'Cancelling Reset...' : 'Approving New Password...'}
          </h2>
          <p className="text-zinc-400 text-sm">Hold tight, this only takes a second.</p>
        </>
      );
    }
    if (status === 'success') {
      return (
        <>
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/20 mb-5">
            <CheckCircle size={42} weight="fill" className="text-emerald-400" />
          </div>
          <h2 className="text-2xl font-unbounded font-bold text-white mb-3">Password Updated! 🎉</h2>
          <p className="text-zinc-300 text-sm mb-6">{message || 'You can now log in with your new password.'}</p>
          <Button
            onClick={() => navigate('/')}
            className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-full py-5"
            data-testid="reset-go-login"
          >
            Go to Login
          </Button>
        </>
      );
    }
    if (status === 'denied') {
      return (
        <>
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-zinc-700/40 mb-5">
            <XCircle size={42} weight="fill" className="text-zinc-300" />
          </div>
          <h2 className="text-2xl font-unbounded font-bold text-white mb-3">Reset Cancelled</h2>
          <p className="text-zinc-400 text-sm mb-6">
            {message || "Your existing password is unchanged. If you didn't request this, your account is safe."}
          </p>
          <Button
            onClick={() => navigate('/')}
            className="w-full bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-full py-5"
            data-testid="reset-back-home"
          >
            Back to Home
          </Button>
        </>
      );
    }
    // error
    return (
      <>
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-500/20 mb-5">
          <Warning size={42} weight="fill" className="text-red-400" />
        </div>
        <h2 className="text-2xl font-unbounded font-bold text-white mb-3">Something Went Wrong</h2>
        <p className="text-zinc-400 text-sm mb-6">{errorDetail}</p>
        <div className="flex flex-col gap-2">
          <Button
            onClick={() => navigate('/')}
            className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-full py-5"
            data-testid="reset-error-home"
          >
            Back to Login
          </Button>
        </div>
      </>
    );
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 noise-bg"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1594886801338-b81548345f77?q=80&w=2000&auto=format&fit=crop)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="reset-approve-page"
    >
      <div className="absolute inset-0 bg-black/80" />
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        <div className="glass-card p-8 text-center">
          {renderState()}
        </div>
      </motion.div>
    </div>
  );
};

export default ResetApprove;
